import { isGatewayFailure, reportReachable } from '@/stores/connection';
import type { components, paths } from './schema';

/**
 * Typed API client.
 *
 * Request and response shapes come from the OpenAPI spec the API emits, so a
 * breaking endpoint change surfaces as a compile error here rather than as a
 * runtime failure in front of a user.
 *
 * The access token is held in memory only. Putting it in localStorage would
 * make it readable by any injected script; the refresh token is an httpOnly
 * cookie the browser sends automatically and JavaScript cannot read at all.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  /** For a 429: how long the server asked to be left alone. */
  readonly retryAfterMs: number | null;

  constructor(status: number, body: unknown, message: string, retryAfterMs: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.retryAfterMs = retryAfterMs;
  }
}

/** Said for a 429: the server's own words are a plain-text line nobody needs to read. */
export const BUSY_MESSAGE = 'The server is busy for a moment. It is tried again by itself.';

/**
 * How many requests the server will still take from this address before its
 * window resets, from the `RateLimit` header it sends with every answer
 * (draft-7: `limit=300, remaining=298, reset=27`). Background work — the
 * offline warm-up — reads it to leave the allowance to the person's screens.
 */
let budget: { remaining: number; resetAt: number } | null = null;

export function rateBudget(): { remaining: number; resetAt: number } | null {
  return budget;
}

function recordRateBudget(response: Response): void {
  const header = response.headers?.get?.('RateLimit');
  if (!header) return;
  const remaining = /remaining=(\d+)/.exec(header);
  const reset = /reset=(\d+)/.exec(header);
  if (remaining && reset) budget = { remaining: Number(remaining[1]), resetAt: Date.now() + Number(reset[1]) * 1000 };
}

/** How long a 429 says to wait: Retry-After, else until the window resets. */
function retryAfterOf(response: Response): number {
  const seconds = Number(response.headers?.get?.('Retry-After'));
  if (Number.isFinite(seconds) && seconds > 0) return seconds * 1000;
  return budget ? Math.max(1000, budget.resetAt - Date.now()) : 30_000;
}

type CurrentUserDto = components['schemas']['CurrentUserDto'];
/** Fields added by .docs/019; optional so a hand-built user still type-checks. */
type NewerUserFields = 'roleNames' | 'twoStepEnabled' | 'tempPasswordExpiresAt' | 'accessHash';
export type CurrentUser = Omit<CurrentUserDto, NewerUserFields> &
  Partial<Pick<CurrentUserDto, NewerUserFields>>;
export type LoginResponse = components['schemas']['LoginResponseDto'];
/** Signing in with two-step on: the password was right, now the code. */
export type TwoStepRequired = components['schemas']['TwoStepRequiredDto'];

let accessToken: string | null = null;

/**
 * Sent with every request once this browser keeps a copy of someone's work:
 * which browser it is and which build, so the server can stop a PC an
 * administrator has revoked, and refuse work from a build too old to trust
 * (.docs/021, .docs/023). Empty until then, so nothing changes before it.
 */
let deviceHeaders: Record<string, string> = {};

export function setDeviceHeaders(headers: Record<string, string>): void {
  deviceHeaders = headers;
}
/**
 * Called when a session cannot be recovered, so the app can return to login.
 * `reason` is the server's own words when it said why, e.g. that the system
 * was reset.
 */
let onSessionLost: ((reason?: string) => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setSessionLostHandler(handler: (reason?: string) => void): void {
  onSessionLost = handler;
}

/**
 * The signed-in person's access hash. Every answer carries the current one as
 * `x-access-hash`; when it differs, someone changed their roles, permissions
 * or farms, and the app re-reads them so the sidebar and buttons are right
 * without a reload (.docs/019).
 */
let knownAccessHash: string | null = null;
let onAccessChanged: (() => void) | null = null;

export function setKnownAccessHash(hash: string | null | undefined): void {
  knownAccessHash = hash ?? null;
}

export function setAccessChangedHandler(handler: () => void): void {
  onAccessChanged = handler;
}

function noticeAccessHash(response: Response): void {
  const hash = response.headers.get('x-access-hash');
  if (!hash || knownAccessHash === null || hash === knownAccessHash) return;
  knownAccessHash = hash;
  onAccessChanged?.();
}

/**
 * Asked when the server wants the person to confirm it is them before a
 * dangerous action (resetting a password, changing roles). Resolves true once
 * they have, and the request is sent again.
 */
let onConfirmRequired: (() => Promise<boolean>) | null = null;

export function setConfirmRequiredHandler(handler: () => Promise<boolean>): void {
  onConfirmRequired = handler;
}

/**
 * What a refresh attempt found. "unreachable" is kept apart from "expired":
 * a network blip must not sign the person out and throw away an open form.
 *
 * "expired" carries `reason` when the server said *why* — a password change,
 * an administrator's action — as opposed to the refresh token simply having
 * run its course, which is too routine to explain.
 */
type RefreshResult =
  | { status: 'ok' }
  | { status: 'expired'; reason?: string | undefined }
  | { status: 'unreachable' };

/** The two sentences a refresh failure is never worth repeating. */
const ROUTINE_EXPIRY = new Set(['Session expired', 'No session']);

/** Prevents a burst of 401s from firing several refreshes at once. */
let refreshInFlight: Promise<RefreshResult> | null = null;

/**
 * Renews the access token from the refresh cookie.
 *
 * Several windows share one cookie. If two renewed at once, the slower would
 * send a token the faster had just replaced, and the server would take it for
 * a copied token. So renewals across windows take turns through the browser's
 * Web Locks; a window that waited then renews with the cookie the other one
 * left. If the server still says another window got there first, this asks
 * once more.
 */
async function refreshSession(): Promise<RefreshResult> {
  refreshInFlight ??= (async (): Promise<RefreshResult> => {
    try {
      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
      return locks
        ? await locks.request('poultry-refresh', () => renewOnce(true))
        : await renewOnce(true);
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function renewOnce(mayRetry: boolean): Promise<RefreshResult> {
  try {
    const res = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });
    if (isGatewayFailure(res.status)) {
      reportReachable(false);
      return { status: 'unreachable' };
    }
    reportReachable(true);
    if (!res.ok) {
      if (res.status === 401) {
        const body = (await res.json().catch(() => null)) as
          | { code?: string; message?: string }
          | null;
        if (mayRetry && body?.code === 'REFRESH_RACE') {
          await new Promise((r) => setTimeout(r, 300));
          return renewOnce(false);
        }
        const reason =
          typeof body?.message === 'string' && !ROUTINE_EXPIRY.has(body.message)
            ? body.message
            : undefined;
        return { status: 'expired', reason };
      }
      return { status: 'expired' };
    }
    const body = (await res.json()) as LoginResponse;
    accessToken = body.accessToken;
    return { status: 'ok' };
  } catch {
    reportReachable(false);
    return { status: 'unreachable' };
  }
}

/**
 * Renews the access token now. For the lock screen: a lock can outlast the
 * 15-minute access token, and requests to /auth/ paths do not refresh by
 * themselves. "expired" also tells the app the session is gone.
 */
export async function renewSession(): Promise<RefreshResult> {
  const result = await refreshSession();
  if (result.status === 'expired') onSessionLost?.(result.reason);
  return result;
}

/**
 * A random id, without relying on `crypto.randomUUID`.
 *
 * `randomUUID` is restricted to secure contexts (HTTPS or localhost) by
 * spec, and a farm-site PC is routinely reached over plain HTTP on the
 * local network — there `crypto.randomUUID` is simply undefined, so calling
 * it throws `TypeError: crypto.randomUUID is not a function`. `getRandomValues`
 * carries no such restriction, so it is the fallback; `Math.random()` is the
 * last resort, which only weakens uniqueness, not correctness — this id is
 * for correlating a browser tab's own requests, not for anything sensitive.
 */
function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Version 4, variant 1 — a well-formed UUID, not just 16 random bytes.
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return (
    `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-` +
    `${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
  );
}

/**
 * Identifies this browser tab to the audit trail's `session_id` column, so
 * entries from several windows open at once (a farm PC routinely has more
 * than one) can be told apart. `sessionStorage` is per-tab by construction,
 * unlike `localStorage`, which every tab in the same origin would share.
 */
function windowId(): string {
  const KEY = 'poultry-ms-window-id';
  try {
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = randomId();
      sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    // Private browsing or a blocked storage API: a fresh id every call still
    // lets the request through, just without cross-request correlation.
    return randomId();
  }
}

/**
 * How far the server's clock is ahead of this machine's, in milliseconds.
 * Farm-site PCs are not guaranteed to have a correct system clock, and the
 * header clock and calendar both need Asia/Karachi wall-clock time to be
 * right regardless. Updated from every response's Date header; 0 (assume
 * the local clock is correct) until the first response arrives.
 */
let serverTimeOffsetMs = 0;

export function getServerTimeOffsetMs(): number {
  return serverTimeOffsetMs;
}

function recordServerTime(response: Response): void {
  const header = response.headers.get('date');
  if (!header) return;
  const serverNow = Date.parse(header);
  if (Number.isNaN(serverNow)) return;
  serverTimeOffsetMs = serverNow - Date.now();
}

export const UNREACHABLE_MESSAGE = 'Cannot reach the server. Check the network and try again.';

async function send(path: string, init: RequestInit): Promise<Response> {
  let response: Response;
  try {
    response = await fetchWithHeaders(path, init);
  } catch (e) {
    // fetch rejects only when no answer came back at all: network down,
    // server stopped, DNS gone. Anything with a status code is an answer.
    const cancelled = e instanceof DOMException && e.name === 'AbortError';
    // Called off on this side: it says nothing about the line.
    if (!cancelled) reportReachable(false);
    throw new ApiError(0, null, cancelled ? 'Cancelled' : UNREACHABLE_MESSAGE);
  }
  reportReachable(!isGatewayFailure(response.status));
  if (isGatewayFailure(response.status)) throw new ApiError(response.status, null, UNREACHABLE_MESSAGE);
  return response;
}

async function fetchWithHeaders(path: string, init: RequestInit): Promise<Response> {
  const response = await fetch(path, {
    ...init,
    // The refresh cookie must travel with every request.
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Window-Id': windowId(),
      ...deviceHeaders,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...init.headers,
    },
  });
  recordServerTime(response);
  recordRateBudget(response);
  noticeAccessHash(response);
  return response;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response = await send(path, init);

  // An expired access token is recoverable: refresh once, then retry. Only the
  // failure of that refresh means the session is genuinely gone.
  if (response.status === 401 && !path.includes('/auth/')) {
    const said = await response
      .clone()
      .json()
      .then((b: { message?: unknown }) => (typeof b?.message === 'string' ? b.message : undefined))
      .catch(() => undefined);
    const refreshed = await refreshSession();
    if (refreshed.status === 'ok') {
      response = await send(path, init);
    } else if (refreshed.status === 'unreachable') {
      throw new ApiError(0, null, UNREACHABLE_MESSAGE);
    } else {
      // The request that was actually denied said more than a bare refresh
      // failure usually does (it carries the session's own revoke reason),
      // so it is preferred; the refresh failure is the fallback.
      onSessionLost?.(said ?? refreshed.reason);
    }
  }

  // A dangerous action on an old sign-in: confirm it is you, then try again.
  if (response.status === 403 && onConfirmRequired) {
    const code = await response
      .clone()
      .json()
      .then((b: { code?: unknown }) => b?.code)
      .catch(() => undefined);
    if (code === 'CONFIRM_REQUIRED' && (await onConfirmRequired())) {
      response = await send(path, init);
    }
  }

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const body: unknown = isJson ? await response.json() : await response.text();

  // The general limit answers in plain text; sign-in's own limit says what to do, and is kept.
  if (response.status === 429) {
    const said = typeof body === 'object' && body !== null && 'message' in body ? String((body as { message: unknown }).message) : null;
    throw new ApiError(429, body, said ?? BUSY_MESSAGE, retryAfterOf(response));
  }

  if (!response.ok) {
    const raw = typeof body === 'object' && body !== null && 'message' in body
      ? (body as { message: unknown }).message
      : undefined;
    // Validation and batch checks answer with a list of problems; show them
    // all, separated so a comma inside one message cannot split it in two.
    const message = Array.isArray(raw)
      ? raw.map(String).join('; ')
      : raw !== undefined
        ? String(raw)
        : `Request failed with status ${response.status}`;
    throw new ApiError(response.status, body, message);
  }

  return body as T;
}

export type GetPaths = {
  [P in keyof paths]: paths[P] extends { get: unknown } ? P : never;
}[keyof paths];

type GetResponse<P extends GetPaths> = paths[P] extends {
  get: { responses: { 200: { content: { 'application/json': infer R } } } };
}
  ? R
  : unknown;

export const api = {
  get: <P extends GetPaths>(path: P, query?: Record<string, string | number | undefined>) => {
    const search = query
      ? '?' +
        new URLSearchParams(
          Object.entries(query)
            .filter(([, v]) => v !== undefined && v !== '')
            .map(([k, v]) => [k, String(v)]),
        )
      : '';
    return request<GetResponse<P>>(`${path}${search}`);
  },

  post: <T>(path: string, payload?: unknown) =>
    request<T>(path, {
      method: 'POST',
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    }),

  patch: <T>(path: string, payload?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    }),

  put: <T>(path: string, payload?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    }),

  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /**
   * One operation from the outbox, sent as it was saved (.docs/023): any
   * method, with the headers that make sending it twice harmless.
   */
  send: <T>(
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    payload: unknown,
    headers: Record<string, string>,
    signal?: AbortSignal,
  ) =>
    request<T>(path, {
      method,
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
      headers,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Sends a file as the raw request body, typed by the file itself (a
   * picture goes as image/jpeg, not JSON). Same session handling as every
   * other call, so an expired token is refreshed and the upload retried.
   */
  upload: <T>(path: string, file: Blob, method: 'PUT' | 'POST' = 'PUT') =>
    request<T>(path, {
      method,
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    }),

  /**
   * Fetches a file the API serves behind sign-in, such as a picture, as a
   * Blob. An `<img src>` cannot do it: the access token lives in memory, not
   * in a cookie, so it has to travel in the header.
   */
  blob: async (path: string): Promise<Blob> => {
    let response = await send(path, { method: 'GET' });
    if (response.status === 401 && (await refreshSession()).status === 'ok') {
      response = await send(path, { method: 'GET' });
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { message?: unknown } | null;
      throw new ApiError(
        response.status,
        body,
        typeof body?.message === 'string' ? body.message : `Request failed with status ${response.status}`,
      );
    }
    return response.blob();
  },

  /**
   * Downloads a CSV (or any file) from the API and hands it to the browser to
   * save. A plain link cannot do it: the access token lives in memory, not in
   * a cookie, so it has to travel in the header like every other request.
   */
  download: async (path: string, filename: string, query?: Record<string, string | undefined>) => {
    const search = query
      ? '?' +
        new URLSearchParams(
          Object.entries(query).filter((e): e is [string, string] => e[1] !== undefined && e[1] !== ''),
        )
      : '';
    let response = await send(`${path}${search}`, { method: 'GET' });
    if (response.status === 401 && (await refreshSession()).status === 'ok') {
      response = await send(`${path}${search}`, { method: 'GET' });
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { message?: unknown } | null;
      throw new ApiError(
        response.status,
        body,
        typeof body?.message === 'string' ? body.message : `Download failed (${response.status})`,
      );
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      URL.revokeObjectURL(url);
    }
  },

  /**
   * Restores a session from the refresh cookie on a cold page load. When the
   * cookie is simply absent or stale this says nothing — that is the ordinary
   * shape of "not signed in" — but when the server named a reason (the
   * password was changed on another device since this tab was last open),
   * it is passed on so the sign-in screen can show it rather than leave the
   * person wondering why they are there.
   */
  restoreSession: async (): Promise<CurrentUser | null> => {
    const refreshed = await refreshSession();
    if (refreshed.status !== 'ok') {
      if (refreshed.status === 'expired' && refreshed.reason) onSessionLost?.(refreshed.reason);
      return null;
    }
    try {
      return await request<CurrentUser>('/api/v1/auth/me');
    } catch {
      return null;
    }
  },
};
